/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { BinaryOperator, BoolOperator, Operation, IdentifierOperand, Term } from '@cedalo/parser';


const { GraphParserContext, SheetReference } = JSG;

const DOT_REF_SYMBOL = '.';

// eslint-disable-next-line eqeqeq
Operation.register(new BoolOperator('<>', (left, right) => left != right), 4); // we need a sheet-parser module!!
Operation.register(new BinaryOperator(DOT_REF_SYMBOL, (/* left, right */) => '#CALC'), 12);

const isFunctionParam = (node, parent) =>
	parent && parent.type === 'function' && (node.type === 'string');

const checkPrefix = (prefix, expr, index) => (name) => {
	if (name.startsWith(prefix)) {
		const substr = expr.substring(index, index + name.length - 1);
		return substr ? name.startsWith(substr.toUpperCase()) : false;
	}
	return false;
};

const isFirst = (node, parent) => node.start === parent.left.start && parent.left.operator == null;

const keepParserFunctions = (keep, functions) => keep.reduce((acc, name) => ({ ...acc, [name]: functions[name] }), {});
const createFunctionStub = (all, { name, display }) => {
	const fn = () => '#CALC';
	fn.display = display;
	all[name] = fn;
	return all;
};

export default class SheetParserContext extends GraphParserContext {
	constructor(functions = []) {
		super();
		this.strict = true;
		const sheetFunctions = functions.reduce(createFunctionStub, {});
		// DL-1587: keep some parser defined functions => NOTE: simply keeping all parser functions didn't work! why???
		const parserFunctions = keepParserFunctions(['MIN', 'MAX'], this.functions);
		this.functions = Object.assign({}, this.functions, sheetFunctions, parserFunctions);
		this.functionNames = Object.keys(this.functions);
	}

	get isSheetParserContext() {
		return true;
	}

	// we may need a sheet-parser module!!
	createIdentifierTerm(node, parent) {
		if (parent && parent.operator === DOT_REF_SYMBOL) {
			// capitalize first part if its a function, mainly used for inbox, inboxdata...
			const str = node.value;
			const identifier = isFirst(node, parent) && this.hasFunction(str) ? str.toUpperCase() : str;
			return new Term(new IdentifierOperand(identifier));
		}
		// might be a (connection-)sheet reference
		if (node.type === 'identifier' && node.value && node.value.indexOf('!') > 0) {
			const sheetref = SheetReference.fromString(this.scope.sheet, node.value);
			return sheetref || new Term(new IdentifierOperand(node.value));
		}

		return super.createIdentifierTerm(node, parent);
	}

	createReferenceTerm(node, parent) {
		return (isFunctionParam(node, parent)) ? undefined : super.createReferenceTerm(node);
	}

	isFunctionPrefix(prefix = '', expr, index) {
		prefix = prefix.toUpperCase();
		const hasPrefix = checkPrefix(prefix, expr, index);
		return this.functionNames.some(hasPrefix);
	}

	doStopAtOperator(opStr, currOpStr) {
		return currOpStr === DOT_REF_SYMBOL && opStr === DOT_REF_SYMBOL;
	}
}
